import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/api';
import { User, Mail, Lock, AlertCircle, Sparkles } from 'lucide-react';
import logo from '../images/RenderSpaceNT.png';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear email error when user starts typing
    if (name === 'email') {
      setEmailError(null);
    }
  };

  // Email validation function
  const validateEmail = (email: string) => {
    if (!email) return true;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError(null);
    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, email: value }));
    setEmailError(null);
    validateEmail(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Validate email format
      if (!validateEmail(formData.email)) {
        setIsLoading(false);
        return;
      }

      // Check if email exists
      const emailCheck = await authService.checkEmail(formData.email);
      if (!emailCheck.exists) {
        setError('No account found with this email address. Please register first.');
        setIsLoading(false);
        return;
      }

      // Attempt login
      const response = await authService.login({
        email: formData.email,
        password: formData.password,
      });

      await login(response.user);
      navigate('/');
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Invalid email or password. Please try again.');
      } else {
        setError('Login failed. Please try again.');
      }
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8 relative">
      <div className="relative sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="relative group">
            <img src={logo} alt="RenderSpace Logo" className="h-16 w-auto transition-transform group-hover:scale-105" />
            <div className="absolute -inset-1 bg-secondary-400/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
        </div>
        <div className="text-center mt-6">
          <div className="flex items-center justify-center mb-2">
            <Sparkles className="text-secondary-400 mr-2" size={20} />
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
              Sign in to your account
            </h2>
            <Sparkles className="text-secondary-400 ml-2" size={20} />
          </div>
          <p className="text-sm text-white/70">
            Or{' '}
            <Link to="/register" className="font-medium text-secondary-400 hover:text-secondary-300 transition-colors">
              create a new account
            </Link>
          </p>
        </div>
      </div>

      <div className="relative mt-6 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="glass-card py-6 sm:py-8 px-4 sm:px-6 lg:px-10 rounded-xl border border-white/10">
          {error && (
            <div className="mb-4 glass border-red-400/30 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle size={16} className="text-red-400" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/80">
                Email Address
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className={`appearance-none block w-full px-3 py-2 rounded-md text-sm sm:text-base transition-all duration-300 ${
                    emailError ? 'border-red-400/50 focus:border-red-400' : 'border-white/20 focus:border-secondary-400/50'
                  }`}
                  value={formData.email}
                  onChange={handleEmailChange}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <Mail size={16} className="text-white/50" />
                </div>
              </div>
              {emailError && (
                <div className="mt-1 flex items-center space-x-1 text-sm text-red-400">
                  <AlertCircle size={14} />
                  <span>{emailError}</span>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/80">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="appearance-none block w-full px-3 py-2 rounded-md text-sm sm:text-base transition-all duration-300 border-white/20 focus:border-secondary-400/50"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <Lock size={16} className="text-white/50" />
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-modern flex justify-center py-2 px-4 rounded-md text-sm sm:text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary-400 mr-2"></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    <Lock size={16} className="mr-2" />
                    Sign in
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/20" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 glass text-white/60 rounded-lg">Need an account?</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/register"
                className="w-full flex justify-center py-2 px-4 glass border border-white/20 rounded-md text-sm sm:text-base font-medium text-white/80 hover:text-white hover:bg-white/5 transition-all duration-300"
              >
                Create Account
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 